import React, { useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  TABLE_SITES_LIST,
  SITE_LIST_ENDPOINT,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { loadAllSite } from "./SiteList.actionCreators";
import { setAllSiteLoadingState } from "./SiteList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader, isEmpty } from "@utils/utils";
import UserInfo from "@utils/UserInfo";
import SiteId from "./components/SiteId";
import SiteAddress from "./components/SiteAddress";
import SiteStatus from "./components/SiteStatus";
import SiteInfo from "./components/SiteInfo";
import CompanyInfo from "./components/CompanyInfo";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import { siteStatus } from "@utils/filterOptions";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    objectId,
    objectType,
    createNewSite,
    countryId,
    mainCountryInfo,
  } = props;

  const [data, setData] = useState([]);
  const [status, setStatus] = useState(1);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const [isAdminSuggestionsLoading, setAdminSuggestionsLoading] =
    useState(false);
  const [adminList, setAdminList] = useState([]);
  const dataURL = SITE_LIST_ENDPOINT;
  const dispatch = useDispatch();
  const prevCountRef = useRef();

  const countryList = UserInfo.countryListForFilter(t);

  const [selectedFilter, setSelectedFilter] = useState([
    {
      column: "country",
      value: {
        value: mainCountryInfo?.value,
        label: t(mainCountryInfo?.label),
      },
    },
  ]);

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "idInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <SiteId
          data={"#" + value.id}
          id={value.id}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("SITE_NAME"),
      title: "SITE_NAME",
      accessor: "siteName",
      Cell: ({ cell: { value } }) => (
        <SiteId
          data={value.name}
          id={value.id}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("COMPANY"),
      title: "COMPANY",
      accessor: "companyInfo",
      Cell: ({ cell: { value } }) => (
        <CompanyInfo
          data={value.name}
          id={value.id}
          isActive={value.isActive}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("SITE_ADDRESS"),
      title: "SITE_ADDRESS",
      accessor: "siteAddress",
      Cell: ({ cell: { value } }) => (
        <SiteAddress
          data={value.address}
          id={value.id}
          isActive={value.isActive}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("STATUS"),
      title: "STATUS",
      accessor: "quoteStatus",
      Cell: ({ cell: { value } }) => (
        <SiteStatus data={value.status} isActive={value.isActive} />
      ),
      disableFilters: true,
    },
    {
      Header: t("SITE_ENQUIRIES"),
      title: "SITE_ENQUIRIES",
      accessor: "enquiryCount",
      Cell: ({ cell: { value } }) => (
        <SiteInfo data={value.count} isActive={value.isActive} />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const siteListByIds = useSelector((state) => state.siteList.byId);
  const siteListAllIds = useSelector((state) => state.siteList.allIds);
  const filteredRecords = useSelector(
    (state) => state.siteList.filteredRecords
  );

  const isSiteListLoading = useSelector((state) => state.siteList.isLoading);

  useEffect(() => {
    setLoading(isSiteListLoading);
  }, [isSiteListLoading]);
  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
      objectId,
      objectType,
    });

    let salesman = [];
    let statusFilters = [];
    let selectedCountries = [];

    if (additionalFilters.length > 0) {
      const salesmanFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "salesman"
      );
      salesman = salesmanFilter?.map((item) => item.value);

      const statusFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "quoteStatus"
      );
      statusFilters = statusFilter?.map((item) => item.value);

      const countryTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      selectedCountries = countryTypeFilter?.map((item) => item.value);
    }
    if (selectedCountries.length === 0) {
      selectedCountries = countryId;
    }

    // Set the loading state
    setLoading(true);
    dispatch(setAllSiteLoadingState());
    dispatch(
      loadAllSite({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status,
        statusFilters,
        salesman,
        objectId,
        objectType,
        selectedCountries,
      })
    );
  };

  useEffect(() => {
    if (siteListAllIds.length > 0) {
      const responseData = siteListAllIds.map((id) => {
        const tempData = { ...siteListByIds[id] };
        tempData.idInfo = {
          id: tempData.id,
          isActive: tempData.isActive,
        };
        tempData.siteName = {
          id: tempData.id,
          name: tempData.name,
          isActive: tempData.isActive,
        };
        tempData.siteAddress = {
          id: tempData.id,
          address: tempData.address,
          isActive: tempData.isActive,
        };
        tempData.companyInfo = {
          id: tempData.company.id,
          name: tempData.company.name,
          isActive: tempData.isActive,
        };
        tempData.quoteStatus = {
          status: tempData.status,
          isActive: tempData.isActive,
        };

        tempData.enquiryCount = {
          count: tempData.enquiriesCount,
          isActive: tempData.isActive,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, siteListAllIds, siteListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = () => {
    const statusNew = status === 1 ? 2 : 1;
    setStatus(statusNew);
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: additionalFilters,
      });
    }
  }, [status, additionalFilters]);

  const getAdmins = (countryIdList) => {
    setAdminSuggestionsLoading(true);
    axios
      .get(
        `/general/search?&limit=-1&countries=[${countryIdList}]&objectTypes=["admin"]`
      )
      .then((response) => {
        const entityGroupList = [];
        if (response?.data?.admins) {
          response.data.admins.map((key) => {
            entityGroupList.push({
              label: key.id + " - " + key.firstName + " " + key.lastName,
              value: key.id,
            });
          });
        }
        setAdminList(entityGroupList);
        setAdminSuggestionsLoading(false);
      })
      .catch((error) => {
        if (window.debugMode) {
          Console.error(
            "Error: an error occurred while fetching admins",
            error
          );
        }
        setAdminList([]);
        setAdminSuggestionsLoading(false);
      });
  };

  useEffect(() => {
    getAdmins(countryId);
  }, []);
  useEffect(() => {
    const previousCountry = prevCountRef.current;
    const countryTypeFilter = additionalFilters.filter(
      (filterItem) => filterItem.column === "country"
    );
    let selectedCountries = countryTypeFilter?.map((item) => item.value);
    if (selectedCountries.length === 0) {
      selectedCountries = [countryId];
    }
    prevCountRef.current = selectedCountries;
    const isSame =
      selectedCountries?.length === previousCountry?.length &&
      selectedCountries.every(
        (value, index) => value === previousCountry[index]
      );
    if (previousCountry?.length > 0 && !isSame) getAdmins(selectedCountries);
  }, [additionalFilters]);

  return (
    <EncloseTable
      type={TABLE_SITES_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={30}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      setAdditionalFilters={setAdditionalFilters}
      additionalFilters={additionalFilters}
      createNewAction={createNewSite}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      selectedFilter={selectedFilter}
      extraFilters={[
        ...(countryList?.length > 1 && objectType !== "companyId"
          ? [
              {
                type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
                label: t("COUNTRY"),
                name: "country",
                labelClass: "site-list__admins-list-label",
                containerClass: "site-list__status-list-dropdown-container",
                options: countryList,
                placeholder: t("SEARCH"),
                defaultOption: "Nothing Selected",
              },
            ]
          : []),
        {
          type: FILTER_TYPE_BUTTON,
          label: "SITES_LIST_TOGGLE_ACTIVE_ONLY",
          name: "users",
          labelClass: "sites-list__statis-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
        {
          type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
          label: t("SITES_FILTER_STATUS"),
          placeholder: t("SEARCH"),
          name: "quoteStatus",
          labelClass: "site-list__admins-list-label",
          containerClass: "site-list__status-list-dropdown-container",
          defaultOption: "Nothing Selected",
          options: siteStatus(t),
        },
        {
          type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
          label: t("SITES_FILTER_SALESMAN"),
          placeholder: t("SEARCH"),
          name: "salesman",
          labelClass: "site-list__admins-list-label",
          containerClass: "site-list__status-list-dropdown-container",
          defaultOption: "Nothing Selected",
          options: adminList,
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  // objectId: PropTypes.number,
  objectType: PropTypes.string,
  updateRefeshStatus: PropTypes.func,
  countryId: PropTypes.number,
  mainCountryInfo: PropTypes.object,
};
PrepareDataTable.defaultProps = {
  updateRefeshStatus: {},
  // objectId: null,
  objectType: null,
  countryId: null,
  mainCountryInfo: null,
};

export default withTranslation()(PrepareDataTable);
